/*
 * How the rest of the module opens a chat's Settings panel (S13 §4). The panel is a view of the
 * shell's details column, so a door anywhere — the band's ⋯ menu and its owners, the status bar's
 * mode chip, the run and base lines' Reset environment…, `?panel=settings|access`, a review's "Share
 * for review" — opens it with
 * one request naming the view and the chat, and never imports the panel itself: the panel is a lazy
 * chunk of its own, and a door drawn on every chat page must not carry it.
 *
 * A door may name the section it is about. The panel scrolls that section into view and, for
 * `owners` with `suggest`, opens the group dialog with the suggested group ticked first (S14 §6's
 * `share_for_review`).
 *
 * This file is plain data on purpose: the details column's registration in `../index.ts` reads it
 * from the entry chunk.
 */

import type { DetailsRequest } from "../../../core/shell/index.ts"

/** The registered id of the Settings panel. */
export const SETTINGS_VIEW = "aichat.settings"

/** The panel's sections, top to bottom; a door names one to land on it. */
export const SETTINGS_SECTIONS = [
	"title",
	"mode",
	"ceiling",
	"owners",
	"assigned",
	"agents",
	"environment",
	"preview",
	"life",
] as const

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number]

/** What a door may ask of the panel beyond the chat. */
export interface SettingsDoor {
	section?: SettingsSection
	/** With `section: "owners"`: open the group dialog with this group offered first. */
	suggest?: number
}

/** The details request that opens the chat's Settings panel. */
export function settingsRequest(key: string, door: SettingsDoor = {}): DetailsRequest {
	const params: Record<string, string | number> = { key }
	if (door.section !== undefined) params["section"] = door.section
	if (door.suggest !== undefined) params["suggest"] = door.suggest
	return { view: SETTINGS_VIEW, params }
}

/** The section a request's parameters name, or undefined for the top of the panel. */
export function sectionOf(params: Record<string, string | number>): SettingsSection | undefined {
	const named = params["section"]
	return SETTINGS_SECTIONS.find((section) => section === named)
}
